import { z } from "zod";
import { personalIdentityNumberTypeSchema } from "./patients";

export const organizationSchema = z.enum([
  "medoma",
  "capioahc",
  "hahag",
  "ksgr",
  "rakus",
  "qa",
  "slso",
  "ssh",
  "sus",
]);
export type IOrganization = z.infer<typeof organizationSchema>;

// https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
export const countryOfOperation = (
  organization: IOrganization,
): "SE" | "CH" | "LV" => {
  switch (organization) {
    case organizationSchema.enum.capioahc:
      return "SE";
    case organizationSchema.enum.medoma:
      return "SE";
    case organizationSchema.enum.qa:
      return "SE";
    case organizationSchema.enum.slso:
      return "SE";
    case organizationSchema.enum.ssh:
      return "SE";
    case organizationSchema.enum.sus:
      return "SE";
    case organizationSchema.enum.hahag:
      return "CH";
    case organizationSchema.enum.ksgr:
      return "CH";
    case organizationSchema.enum.rakus:
      return "LV";
    default:
      throw organization satisfies never;
  }
};

export const timeZoneSchema = z.enum([
  "Europe/Riga",
  "Europe/Stockholm",
  "Europe/Zurich",
]);
export type ITimeZone = z.infer<typeof timeZoneSchema>;

// The time zone the organization's dates and times are expressed in. Mirrors the backend's
// MEDOMA_LOCAL_TIME_ZONE for each deployment.
export const timeZoneOfOperation = (organization: IOrganization): ITimeZone => {
  switch (organization) {
    case organizationSchema.enum.capioahc:
    case organizationSchema.enum.medoma:
    case organizationSchema.enum.qa:
    case organizationSchema.enum.slso:
    case organizationSchema.enum.ssh:
    case organizationSchema.enum.sus:
      return timeZoneSchema.enum["Europe/Stockholm"];
    case organizationSchema.enum.hahag:
    case organizationSchema.enum.ksgr:
      return timeZoneSchema.enum["Europe/Zurich"];
    case organizationSchema.enum.rakus:
      return timeZoneSchema.enum["Europe/Riga"];
    default:
      throw organization satisfies never;
  }
};

export const primaryPatientIdentifierType = (organization: IOrganization) => {
  switch (organization) {
    case organizationSchema.enum.capioahc:
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    case organizationSchema.enum.medoma:
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    case organizationSchema.enum.slso:
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    case organizationSchema.enum.ssh:
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    case organizationSchema.enum.sus:
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    case organizationSchema.enum.qa:
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    case organizationSchema.enum.hahag:
      return personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber;
    case organizationSchema.enum.ksgr:
      return personalIdentityNumberTypeSchema.enum.SwissPatientNumber;
    case organizationSchema.enum.rakus:
      return personalIdentityNumberTypeSchema.enum.LatvianPersonasKods;
    default:
      throw organization satisfies never;
  }
};

export const manualInvoicingEnabled = (
  organization: IOrganization,
): boolean => {
  switch (organization) {
    case organizationSchema.enum.sus:
      return true;
    case organizationSchema.enum.capioahc:
    case organizationSchema.enum.medoma:
    case organizationSchema.enum.qa:
    case organizationSchema.enum.slso:
    case organizationSchema.enum.ssh:
    case organizationSchema.enum.hahag:
    case organizationSchema.enum.ksgr:
    case organizationSchema.enum.rakus:
      return false;
    default:
      throw organization satisfies never;
  }
};

export const organizationSupportsInvoicing = (
  organization: IOrganization,
): boolean => {
  switch (organization) {
    case organizationSchema.enum.sus:
    case organizationSchema.enum.slso:
    case organizationSchema.enum.ssh:
    case organizationSchema.enum.capioahc:
    case organizationSchema.enum.medoma:
    case organizationSchema.enum.qa:
      return true;
    case organizationSchema.enum.hahag:
    case organizationSchema.enum.ksgr:
    case organizationSchema.enum.rakus:
      return false;
    default:
      throw organization satisfies never;
  }
};

// SUS uses the invoicing module, but without invoicing codes
export const organizationUsesInvoicingCodes = (
  organization: IOrganization,
): boolean =>
  organizationSupportsInvoicing(organization) &&
  organization !== organizationSchema.enum.sus;
